/**
 * Connections · Repositories (`/services/repositories`): one row per
 * repository environment, grouped by what the reader owes it (Needs you,
 * Setting up, Working, Not set up), and the environment in the sheet. The open
 * record is the address: `?repository=owner/name` (or `?repo=<id>`) opens it,
 * so a link to one repository's environment lands on it.
 *
 * Workspace variables sit under the list as the dashed line every
 * repository's builds read; Set variable opens them in the sheet.
 */
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Btn, Group, Groups, RecordHead, Row, SheetFoot } from "../queue/index.js";
import { useOwnedSheet } from "../shell/useOwnedSheet.js";
import { useRepositoryEnvironment, type Repository } from "../settings/useRepositoryEnvironment.js";
import { WorkspaceVariables } from "../settings/WorkspaceVariables.js";
import { useWorkspaceVariables } from "../settings/workspaceVariablesApi.js";
import type { ConnectionsTabProps } from "./ConnectionsPage.js";
import { RepositoryEnvironmentSheet } from "./RepoSheet.js";
import { groupRepositories, repoDot, repoReason, repoTag, repoTeamKey, repoVerb } from "./repositoriesModel.js";

type WorkspaceVariablesState = ReturnType<typeof useWorkspaceVariables>;

/** The workspace's variables in the sheet: names and when each was set; values are write-only. */
export function WorkspaceVariablesSheet({ workspace, onClose }: { workspace: WorkspaceVariablesState; onClose: () => void }) {
  return (
    <>
      <RecordHead
        recordKey="WORKSPACE"
        title="Workspace variables"
        onClose={onClose}
        noExpand
        meta={
          <span className="fact" style={{ fontSize: 11.5 }}>
            Read by every repository's builds. A repository's own value wins.
          </span>
        }
      />
      <div className="sbody qa-sheet" data-testid="workspace-variables-sheet">
        <div className="wrk-settings">
          <WorkspaceVariables
            variables={workspace.variables}
            loaded={workspace.loaded}
            error={workspace.error}
            reload={workspace.reload}
            open
            onToggle={() => undefined}
          />
        </div>
      </div>
      <SheetFoot
        why={
          <>
            Values are stored encrypted and <b>never shown again</b>.
          </>
        }
        primary={{ label: "Done", onClick: onClose }}
      />
    </>
  );
}

export function RepositoriesTab({ refreshKey }: ConnectionsTabProps) {
  const env = useRepositoryEnvironment();
  const workspace = useWorkspaceVariables();
  const [params, setParams] = useSearchParams();
  const [variablesOpen, setVariablesOpen] = useState(false);
  const recordOpen = Boolean(params.get("repo") || params.get("repository"));

  const firstRefresh = useRef(refreshKey);
  useEffect(() => {
    if (firstRefresh.current === refreshKey) return;
    firstRefresh.current = refreshKey;
    env.retryVerification();
    void env.reread();
    void workspace.reload();
  }, [refreshKey]);

  const closeRecord = () => setParams({}, { replace: true });
  const sheet = variablesOpen ? (
    <WorkspaceVariablesSheet workspace={workspace} onClose={() => setVariablesOpen(false)} />
  ) : recordOpen ? (
    <RepositoryEnvironmentSheet env={env} onClose={closeRecord} />
  ) : null;
  useOwnedSheet(sheet, () => {
    if (variablesOpen) setVariablesOpen(false);
    else if (recordOpen) closeRecord();
  });

  const groups = groupRepositories(env.repositories);
  const row = (item: Repository) => {
    // The open record carries the freshest view; the list the rest.
    const environment = item.id === env.selected && env.view?.repoId === item.id ? env.view : item.environment;
    const dot = repoDot(environment.state);
    return (
      <Row
        key={item.id}
        glyph={
          <span
            className={dot.mode ? `sdot ${dot.mode}` : "sdot"}
            style={{ "--c": dot.color } as CSSProperties}
            aria-hidden
          />
        }
        rowKey={repoTeamKey(environment)}
        title={`${item.owner}/${item.name}`}
        tag={{ label: repoTag(environment), color: dot.color }}
        state={repoReason({ ...item, environment })}
        verb={repoVerb(environment)}
        sel={recordOpen && !variablesOpen && env.selected === item.id}
        onOpen={() => {
          setVariablesOpen(false);
          setParams({ repository: `${item.owner}/${item.name}` });
        }}
      />
    );
  };

  const names = workspace.variables.map((variable) => variable.name);

  return (
    <Groups>
      {env.error && !recordOpen && (
        <div className="qnote err" role="alert">
          <span>{env.error}</span>
        </div>
      )}
      {env.loaded && env.repositories.length === 0 && !env.error ? (
        <div className="empty dashed tight" data-testid="repositories-empty">
          <p>
            Connect a GitHub repository to configure its environment. <Link to="/services">Open Connections</Link>
          </p>
        </div>
      ) : (
        <>
          <Group
            label="Needs you"
            tone="needs"
            empty={env.loaded ? "Every environment is set up or on its way." : "Reading the repositories."}
          >
            {groups.needs.map(row)}
          </Group>
          <Group label="Setting up" tone="live">
            {groups.setting.map(row)}
          </Group>
          <Group label="Working" plain>
            {groups.working.map(row)}
          </Group>
          <Group label="Not set up" tone="done">
            {groups.notSetUp.map(row)}
          </Group>
        </>
      )}
      <div className="qline dashed" data-testid="workspace-variables-line">
        <span>
          <b>Workspace variables</b> · read by every repository's builds
          {workspace.loaded ? ` · ${names.length ? names.join(", ") : "none yet"}` : ""}
        </span>
        <span className="acts">
          <Btn variant="quiet" size="sm" onClick={() => setVariablesOpen(true)}>
            Set variable
          </Btn>
        </span>
      </div>
    </Groups>
  );
}
